import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { ShieldCheck, LogOut } from "lucide-react";

/**
 * Enforces TOTP MFA (AAL2) for the current admin session.
 * - If no TOTP factor is enrolled → shows QR enrollment then verifies.
 * - If a factor is enrolled but session is AAL1 → prompts for a 6-digit code.
 */
const MFAGate = ({ children }: { children: React.ReactNode }) => {
  const { aal, refreshAal, signOut } = useAuth();
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<"enroll" | "challenge" | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setError("");
      if (aal === "aal2") { setLoading(false); return; }

      const { data: factorsData, error: fErr } = await supabase.auth.mfa.listFactors();
      if (fErr) { setError(fErr.message); setLoading(false); return; }

      const verified = factorsData?.totp?.find((f) => f.status === "verified");
      if (verified) {
        setFactorId(verified.id);
        setMode("challenge");
        setLoading(false);
        return;
      }

      // Clean up any unverified factors before enrolling a fresh one
      const unverified = factorsData?.totp?.filter((f) => f.status !== "verified") ?? [];
      for (const f of unverified) {
        await supabase.auth.mfa.unenroll({ factorId: f.id });
      }

      const { data: enrollData, error: eErr } = await supabase.auth.mfa.enroll({
        factorType: "totp",
        friendlyName: `Admin ${Date.now()}`,
      });
      if (eErr) { setError(eErr.message); setLoading(false); return; }
      setFactorId(enrollData.id);
      setQr(enrollData.totp.qr_code);
      setSecret(enrollData.totp.secret);
      setMode("enroll");
      setLoading(false);
    };
    init();
  }, [aal]);

  const verify = async () => {
    if (!factorId || code.length < 6) return;
    setBusy(true); setError("");
    const { data: ch, error: cErr } = await supabase.auth.mfa.challenge({ factorId });
    if (cErr) { setError(cErr.message); setBusy(false); return; }
    const { error: vErr } = await supabase.auth.mfa.verify({
      factorId, challengeId: ch.id, code,
    });
    if (vErr) { setError(vErr.message); setBusy(false); return; }
    await refreshAal();
    setBusy(false);
    setCode("");
  };

  if (aal === "aal2") return <>{children}</>;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-card border border-border rounded-xl p-8 shadow-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-lg">Two-factor authentication</h1>
            <p className="text-xs text-muted-foreground">Required for admin access</p>
          </div>
        </div>

        {mode === "enroll" && qr && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Scan this QR code with Google Authenticator, 1Password, Authy, or any TOTP app, then enter the 6-digit code to enable MFA.
            </p>
            <div className="flex justify-center bg-white rounded-lg p-4">
              <img src={qr} alt="TOTP QR code" className="h-44 w-44" />
            </div>
            {secret && (
              <p className="text-xs text-muted-foreground text-center break-all">
                Or enter manually: <span className="font-mono">{secret}</span>
              </p>
            )}
          </div>
        )}

        {mode === "challenge" && (
          <p className="text-sm text-muted-foreground mb-4">
            Enter the 6-digit code from your authenticator app to continue.
          </p>
        )}

        <div className="mt-5 space-y-3">
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            className="w-full h-12 px-4 rounded-lg border border-input bg-background text-foreground text-center text-lg font-mono tracking-[0.4em] focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder="••••••"
          />
          {error && <div className="text-destructive text-sm">{error}</div>}
          <Button onClick={verify} disabled={busy || code.length < 6} className="w-full" size="lg">
            {busy ? "Verifying..." : "Verify & continue"}
          </Button>
          <Button onClick={signOut} variant="ghost" className="w-full" size="sm">
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MFAGate;
